import type { ButtonHTMLAttributes } from "react";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled, { css } from "styled-components";

export type ToggleSize = "default" | "compact";

export interface ToggleProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "type"
> {
  checked: boolean;
  label?: string;
  size?: ToggleSize;
  onChange?: (checked: boolean) => void;
}

const SIZE_CONFIG: Record<
  ToggleSize,
  { height: number; knob: number; padding: number; width: number }
> = {
  default: {
    height: 32,
    knob: 24,
    padding: 4,
    width: 56,
  },
  compact: {
    height: 28,
    knob: 20,
    padding: 4,
    width: 48,
  },
};

export const Toggle = ({
  checked,
  disabled,
  label,
  onChange,
  size = "default",
  ...rest
}: ToggleProps): JSX.Element => {
  const handleClick = (): void => {
    if (disabled) return;
    onChange?.(!checked);
  };

  return (
    <Label $disabled={Boolean(disabled)}>
      {label && <Text $disabled={Boolean(disabled)}>{label}</Text>}
      <Switch
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        $checked={checked}
        $size={size}
        onClick={handleClick}
        {...rest}
      >
        <Knob $checked={checked} $size={size} />
      </Switch>
    </Label>
  );
};

const Label = styled.div<{ $disabled: boolean }>`
  align-items: center;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  display: inline-flex;
  gap: 12px;
`;

const Text = styled.span<{ $disabled: boolean }>`
  ${TypographyV2.b1}
  color: ${(props) => (props.$disabled ? ColorV2.Blue5 : ColorV2.Blue1)};
`;

const Switch = styled.button<{
  $checked: boolean;
  $size: ToggleSize;
}>`
  align-items: center;
  background: ${(props) => (props.$checked ? ColorV2.Blue1 : ColorV2.Blue6)};
  border: 1.5px solid
    ${(props) => (props.$checked ? ColorV2.Blue1 : ColorV2.Blue6)};
  border-radius: 999px;
  display: inline-flex;
  flex-shrink: 0;
  height: ${(props) => SIZE_CONFIG[props.$size].height}px;
  padding: ${(props) => SIZE_CONFIG[props.$size].padding}px;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  width: ${(props) => SIZE_CONFIG[props.$size].width}px;

  &:hover:not(:disabled) {
    cursor: pointer;
  }

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue3};
    outline-offset: 2px;
  }

  &:disabled {
    background: ${ColorV2.Blue7};
    border-color: ${ColorV2.Blue6};
    cursor: not-allowed;
  }
`;

const Knob = styled.span<{
  $checked: boolean;
  $size: ToggleSize;
}>`
  background: ${ColorV2.White};
  border-radius: 999px;
  display: block;
  height: ${(props) => SIZE_CONFIG[props.$size].knob}px;
  transform: translateX(0);
  transition: transform 0.15s ease;
  width: ${(props) => SIZE_CONFIG[props.$size].knob}px;

  ${(props) =>
    props.$checked &&
    css`
      transform: translateX(
        ${SIZE_CONFIG[props.$size].width -
        SIZE_CONFIG[props.$size].knob -
        SIZE_CONFIG[props.$size].padding * 2}px
      );
    `}
`;
